import { useI18n } from 'next-localization';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { useState, useRef } from 'react';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import styles from './ShareModalContent.module.scss';
import {
  EmailIcon,
  FacebookIcon,
  LinkedinIcon,
  XIcon,
  WhatsappIcon,
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share';
import { useRouter } from 'next/router';

interface ShareModalContentProps {
  onCloseModal: () => void;
}

const ShareModalContent = ({ onCloseModal }: ShareModalContentProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const { metaDescription, metaTitle } = sitecoreContext;
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const currentArticleName = router.asPath.split('/').at(-1);

  const pageUrl =
    (sitecoreContext.languageVersions.find(
      (langVersion) =>
        langVersion.hreflang.toLowerCase() === sitecoreContext.language?.toLowerCase()
    )?.href ?? '') + (currentArticleName ? '/' + currentArticleName : '');

  const meta = {
    pageUrl,
    metaTitle,
    metaDescription,
  };

  const copyLink = () => {
    inputRef.current?.select();
    document.execCommand('copy');
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className={styles['share-modal']}>
      <div className="d-flex align-items-center justify-content-between p-2">
        <h5 className="subheader-2">{t('shareThisArticle')}</h5>
        <button
          aria-label={t('closeModal')}
          className={`btn ${styles['close-button']}`}
          onClick={onCloseModal}
        >
          <i className="bi bi-x" aria-hidden={true} />
        </button>
      </div>
      <div
        className={`${styles['share-modal__content']} d-flex flex-column align-items-start p-2 pt-3`}
      >
        <h6 className="section-header-small">{t('copyLink')}</h6>
        <p title={meta.pageUrl} className={`h6 link mt-1 ${styles['link']}`}>
          {meta.pageUrl}
        </p>
        <input
          ref={inputRef}
          aria-label="current url"
          className={styles['link-input']}
          type="text"
          readOnly
          tabIndex="-1"
          value={meta.pageUrl}
        />
        <AbrdnButton
          buttonType="primary"
          className={`mt-2 ${styles['copy-btn']}`}
          isSuccess={isCopied}
          onClick={copyLink}
        >
          <i className={`bi bi-${isCopied ? 'bi-check2' : 'copy'} me-1`} aria-hidden={true} />
          <span>{isCopied ? t('copied') : t('copyLink')}</span>
        </AbrdnButton>
      </div>
      <div className="d-flex flex-column align-items-start p-2">
        <h6 className="section-header-small">{t('shareTo')}</h6>
        <div className="socials-list d-flex align-items-center flex-wrap">
          <FacebookShareButton
            url={meta.pageUrl}
            htmlTitle={meta.metaTitle}
            className={`me-1 mt-1 ${styles['share-btn']}`}
          >
            <span className="sr-only">{t('shareThisArticle')}</span>
            <FacebookIcon round size={48} bgStyle={{ fill: 'black' }} aria-hidden={true} />
          </FacebookShareButton>
          <LinkedinShareButton
            url={meta.pageUrl}
            summary={meta.metaDescription}
            title={meta.metaTitle}
            htmlTitle={meta.metaTitle}
            className={`me-1 mt-1 ${styles['share-btn']}`}
          >
            <span className="sr-only">{t('shareThisArticle')}</span>
            <LinkedinIcon round size={48} bgStyle={{ fill: 'black' }} aria-hidden={true} />
          </LinkedinShareButton>
          <TwitterShareButton
            url={meta.pageUrl}
            htmlTitle={meta.metaTitle}
            title={meta.metaTitle}
            className={`me-1 mt-1 ${styles['share-btn']}`}
          >
            <span className="sr-only">{t('shareThisArticle')}</span>
            <XIcon round size={48} bgStyle={{ fill: 'black' }} aria-hidden={true} />
          </TwitterShareButton>
          <WhatsappShareButton
            url={meta.pageUrl}
            htmlTitle={meta.metaTitle}
            title={meta.metaTitle}
            className={`me-1 mt-1 ${styles['share-btn']}`}
          >
            <span className="sr-only">{t('shareThisArticle')}</span>
            <WhatsappIcon round size={48} bgStyle={{ fill: 'black' }} aria-hidden={true} />
          </WhatsappShareButton>
          <EmailShareButton
            url={meta.pageUrl}
            subject={meta.metaTitle}
            body={meta.metaDescription}
            htmlTitle={meta.metaTitle}
            className={`mt-1 ${styles['share-btn']}`}
          >
            <span className="sr-only">{t('shareThisArticle')}</span>
            <EmailIcon round size={48} bgStyle={{ fill: 'black' }} aria-hidden={true} />
          </EmailShareButton>
        </div>
      </div>
    </div>
  );
};

export default ShareModalContent;
